import { Calculation } from '@carbon/react/icons';
import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import { LazyUnitInterestCard } from 'components/lazy-unit-interest-card';
import { NRIVerification } from 'components/nri-table/nri-verification';
import { CalculationType } from 'components/nri-table/types';
import { UatNriMatrixCard } from 'components/uat-nri-matrix-card';
import { isNil } from 'lodash-es';
import { SlotNRIVerifications } from 'types/api-responses';
import styles from './uat-interest-info.module.scss';
import { stampIsPending } from '../tract-intersection-analysis/acreage-basis-atoms';
import { UatAcreageBasisPanel } from './uat-acreage-basis-panel';

const NRI_FORMATTER = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 8,
});

// Render trigger for calculation icon
const renderCalculationTrigger = (active: boolean, toggle: () => void) => (
  <CalculationIconTrigger
    definition="Show Calculation"
    active={active}
    onClick={toggle}
  >
    <div className={styles.iconContainer}>
      <Calculation aria-hidden="true" role="img" />
    </div>
  </CalculationIconTrigger>
);

interface UatInterestInfoProps {
  /** Owning subtract; the acreage basis is stored per (subtract, UAT). */
  subtractId?: string;
  /** Addresses the acreage parameter endpoint. */
  uatGisUuid?: string;
  /** Only legal basis exposes the overlap editor. */
  acreageBasis?: 'gis' | 'legal';
  /**
   * The overlap as stored on the parameter row. Preferred over the value on the
   * formula payload, which is only rewritten by the background recalculation and
   * so lags a save by a few seconds.
   */
  storedOverlapFraction?: number | null;
  uatNri: number | null;
  uatWi: number | null;
  nriCalculationId: number | null;
  /** Verification data from the subtract; drives the inline checkmark/X controls. */
  verifications?: SlotNRIVerifications;
}

interface UatInterestRowProps {
  label: string;
  value: number | null;
  nriCalculationId: number | null;
  /** 'nri' or 'wi' — selects the matching verification item and calculation type. */
  type: 'nri' | 'wi';
  verifications?: SlotNRIVerifications;
  subtractId?: string;
  uatGisUuid?: string;
  acreageBasis?: 'gis' | 'legal';
  /**
   * The overlap as stored on the parameter row. Preferred over the value on the
   * formula payload, which is only rewritten by the background recalculation and
   * so lags a save by a few seconds.
   */
  storedOverlapFraction?: number | null;
}

/**
 * One UAT NRI or WI row matching the NRI Review table layout:
 * value → calculation icon → matrix icon → verification controls.
 *
 * @param {UatInterestRowProps} props - The component props.
 * @return {JSX.Element} The row element.
 */
const UatInterestRow = ({
  label,
  value,
  nriCalculationId,
  type,
  verifications,
  subtractId,
  uatGisUuid,
  acreageBasis,
  storedOverlapFraction,
}: UatInterestRowProps): JSX.Element => {
  const verification =
    type === 'nri' ? verifications?.nri : verifications?.workingInterest;
  const calculationType: CalculationType = type === 'nri' ? 'NRI' : 'WI';
  const display = value !== null ? NRI_FORMATTER.format(value) : '--';

  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>{display}</span>
      {nriCalculationId && (
        <LazyUnitInterestCard
          nriCalculationId={nriCalculationId}
          unitInterestValue={value}
          calculationType={calculationType}
          renderTrigger={renderCalculationTrigger}
          renderAcreageBasisPanel={
            type === 'nri' &&
            acreageBasis === 'legal' &&
            subtractId &&
            uatGisUuid
              ? (nriFormula, close) => (
                  <UatAcreageBasisPanel
                    subtractId={subtractId}
                    uatGisUuid={uatGisUuid}
                    nriCalculationId={nriCalculationId}
                    onDone={close}
                    tractGrossAc={nriFormula.tractGrossAc ?? null}
                    uatPlatAc={nriFormula.uatPlatAc ?? null}
                    overlapFraction={
                      storedOverlapFraction ??
                      nriFormula.overlapFraction ??
                      null
                    }
                    gisOverlapFraction={nriFormula.gisOverlapFraction ?? null}
                    recalculating={stampIsPending({
                      chosenBasis: acreageBasis,
                      stampedBasis: nriFormula.acreageBasis,
                      storedOverlapFraction,
                      stampedOverlapFraction: nriFormula.overlapFraction,
                    })}
                    warnings={nriFormula.acreageBasisWarnings ?? []}
                    // Server-owned rule inputs (#7418 review): without these
                    // the panel falls back to the hand-mirrored constants and
                    // a server-side change desyncs silently.
                    overlapTolerance={nriFormula.overlapTolerance}
                    driftPrecision={nriFormula.driftPrecision}
                  />
                )
              : undefined
          }
        />
      )}
      {nriCalculationId && (
        <UatNriMatrixCard
          nriCalculationId={nriCalculationId}
          calculationType={calculationType}
        />
      )}
      {verification && nriCalculationId && (
        <NRIVerification
          nriId={`${nriCalculationId}-${type}`}
          disableActions={isNil(value)}
          eventsHref={verification.href.events}
          commentsHref={verification.href.comments}
          initialStatus={verification.status}
          initialCommentsCount={verification.commentsCount}
        />
      )}
    </div>
  );
};

/**
 * Right-side "Interest Info" card on each UAT row of the Current
 * Development drill-down. Shows UAT NRI and WI values with calculation
 * breakdown, NRI matrix popup, and interactive verification controls
 * matching the NRI Review table pattern.
 *
 * @param {UatInterestInfoProps} props - The component props.
 * @return {JSX.Element} The UAT interest card.
 */
export const UatInterestInfo = ({
  uatNri,
  uatWi,
  nriCalculationId,
  verifications,
  subtractId,
  uatGisUuid,
  acreageBasis,
  storedOverlapFraction,
}: UatInterestInfoProps): JSX.Element => (
  <div className={styles.container}>
    <span className={styles.title}>Interest Info</span>
    <UatInterestRow
      type="nri"
      label="UAT NRI:"
      value={uatNri}
      nriCalculationId={nriCalculationId}
      verifications={verifications}
      subtractId={subtractId}
      uatGisUuid={uatGisUuid}
      acreageBasis={acreageBasis}
      storedOverlapFraction={storedOverlapFraction}
    />
    <UatInterestRow
      type="wi"
      label="UAT WI:"
      value={uatWi}
      nriCalculationId={nriCalculationId}
      verifications={verifications}
      subtractId={subtractId}
      uatGisUuid={uatGisUuid}
      acreageBasis={acreageBasis}
      storedOverlapFraction={storedOverlapFraction}
    />
  </div>
);
